import { ParamsService } from './../../../auxiliares/servicios/params.service';
import {
  Component,
  Input,
  OnInit,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';
import { ICamara, ICamaraAlarma, IFilter, IQueryParam } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { StreamCamaraComponent } from '../stream-camara/stream-camara.component';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { AuxiliaresModule } from '../../../auxiliares/auxiliares.module';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Lista2Service } from '../../../auxiliares/servicios/lista2.service';
import {
  MultiCameraStreamComponent,
  CameraStream,
} from '../../../auxiliares/multi-camera-stream/multi-camera-stream.component';
import { MultiCameraMosaicComponent } from '../../../auxiliares/multi-camera-mosaic/multi-camera-mosaic.component';
import { DatePickerComponent } from '../../../componentes/date-picker/date-picker.component';

export interface CamaraRes {
  camara: ICamara;
  canal: string;
  tipo?: string; // para identificar el tipo de camara
}

export interface CamarasMultipleData {
  data: string[];
  zonaInicial?: ICamaraAlarma;
  fechaEvento?: string;
  nombreEntidad?: string;
  popupMapa?: boolean;
}
@Component({
  selector: 'app-camaras-multiple',
  templateUrl: './camaras-multiple.component.html',
  styleUrl: './camaras-multiple.component.scss',
  standalone: true,
  imports: [
    StreamCamaraComponent,
    CommonModule,
    AuxiliaresModule,
    MultiCameraStreamComponent,
    MultiCameraMosaicComponent,
    DatePickerComponent,
  ],
})
export class CamarasMultipleComponent implements OnInit {
  dialogRef = inject<MatDialogRef<StreamCamaraComponent>>(MatDialogRef, {
    optional: true,
  });
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  paramsService = inject(ParamsService);
  private cdk = inject(OverlayContainer);
  private el = inject(ElementRef);
  dataDialog = inject(MAT_DIALOG_DATA, { optional: true });

  public loading: boolean = true;
  public camarasRes: CamaraRes[] = [];
  public camaraActual: ICamara;
  public canalActual: string;
  public maximizado: boolean = false;
  public fecha: string;
  public dragBoundary: HTMLElement;
  public vivo: boolean = true;
  public header = false;
  public fechaSeleccionadaISO: string | null = null;
  public maxFechaDate: Date = new Date();

  // Control de paginación personalizada

  public currentPageIndex: number = 0;
  @Input() public pageSize: number = 4;

  // Para el componente multi-camera-stream
  public streamsParaGrid: CameraStream[] = [];
  public mosaico: boolean = false;
  /** Cámara con la que abre el mosaico en modo 1 cámara (zona del evento). */
  public mosaicoInicial?: { idCamara: string; canal: string };

  // Getter para obtener los streams paginados
  public get streamsPaginados(): CameraStream[] {
    if (!this.streamsParaGrid.length) {
      return this.streamsParaGrid;
    }

    const startIndex = this.currentPageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.streamsParaGrid.slice(startIndex, endIndex);
  }

  public onCameraClick(stream: CameraStream): void {
    this.select(stream.camara, stream.canal);
  }

  static open(
    dialog: MatDialog,
    data: CamarasMultipleData,
    extraConfig?: Partial<MatDialogConfig>,
  ): MatDialogRef<CamarasMultipleComponent> {
    return dialog.open(CamarasMultipleComponent, {
      data,
      width: '75vw',
      maxWidth: '900px',
      height: 'auto',
      maxHeight: '90vh',
      ...extraConfig,
    });
  }

  // === MÉTODOS DE NAVEGACIÓN PERSONALIZADA ===

  // Obtener el texto del rango actual (ej: "1-4 de 12 cámaras" o "1 de 1 cámara")
  public getCurrentRangeText(): string {
    const totalCameras = this.streamsParaGrid.length;
    if (totalCameras === 0) return '0 cámaras';

    const start = this.currentPageIndex * this.pageSize + 1;
    const end = Math.min(
      (this.currentPageIndex + 1) * this.pageSize,
      totalCameras,
    );

    // Modo popup: solo mostrar "1-4" o "2-4" sin "de X cámaras"
    if (this.popupMapa) {
      const totalPages = Math.ceil(totalCameras / this.pageSize);
      return `${this.currentPageIndex + 1}-${totalPages}`;
    }

    // Si solo hay una cámara o el rango es de una sola, mostrar solo el número
    if (start === end) {
      return `${start} de ${totalCameras} ${totalCameras === 1 ? 'cámara' : 'cámaras'}`;
    }

    return `${start}-${end} de ${totalCameras} cámaras`;
  }

  // Obtener el número de página actual (0-based)
  public getCurrentPageNumber(): number {
    return this.currentPageIndex;
  }

  // Obtener array de números de página para los indicadores
  public getPageNumbers(): number[] {
    const totalPages = Math.ceil(this.streamsParaGrid.length / this.pageSize);
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  // Navegación: ¿Es la primera página?
  public isFirstPage(): boolean {
    return this.currentPageIndex === 0;
  }

  // Navegación: ¿Es la última página?
  public isLastPage(): boolean {
    const totalPages = Math.ceil(this.streamsParaGrid.length / this.pageSize);
    return this.currentPageIndex >= totalPages - 1;
  }

  // Ir a una página específica
  public goToPage(pageIndex: number): void {
    const totalPages = Math.ceil(this.streamsParaGrid.length / this.pageSize);
    if (pageIndex >= 0 && pageIndex < totalPages) {
      this.currentPageIndex = pageIndex;
    }
  }

  // Ir a la primera página
  public goToFirstPage(): void {
    this.currentPageIndex = 0;
  }

  // Ir a la última página
  public goToLastPage(): void {
    const totalPages = Math.ceil(this.streamsParaGrid.length / this.pageSize);
    this.currentPageIndex = Math.max(0, totalPages - 1);
  }

  // Ir a la página anterior
  public goToPreviousPage(): void {
    if (this.currentPageIndex > 0) {
      this.currentPageIndex--;
    }
  }

  // Ir a la página siguiente
  public goToNextPage(): void {
    const totalPages = Math.ceil(this.streamsParaGrid.length / this.pageSize);
    if (this.currentPageIndex < totalPages - 1) {
      this.currentPageIndex++;
    }
  }

  // Obtener número de columnas del grid según el tamaño de pantalla y número de cámaras
  public getGridColumns(): number {
    const screenWidth = window.innerWidth;

    // Para diálogos y vistas normales: siempre 2 columnas (grid 2x2)
    // Esto muestra hasta 4 cámaras en un layout 2x2
    if (screenWidth >= 768) {
      return 2; // 2 columnas = layout 2x2 para 4 cámaras
    }
    // Para móviles: 1 columna
    else {
      return 1;
    }
  }

  // Soporte para navegación con teclado
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Solo activar si no está maximizado y hay múltiples páginas. En mosaico
    // el teclado lo maneja app-multi-camera-mosaic.
    if (this.maximizado || this.mosaico || !this.hasMultiplePages()) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.goToPreviousPage();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.goToNextPage();
        break;
      case 'Home':
        event.preventDefault();
        this.goToFirstPage();
        break;
      case 'End':
        event.preventDefault();
        this.goToLastPage();
        break;
      // Números 1-9 para ir directamente a una página
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        const pageNum = parseInt(event.key) - 1;
        const totalPages = Math.ceil(
          this.streamsParaGrid.length / this.pageSize,
        );
        if (pageNum < totalPages) {
          event.preventDefault();
          this.goToPage(pageNum);
        }
        break;
    }
  }

  // Verificar si hay múltiples páginas
  private hasMultiplePages(): boolean {
    return Math.ceil(this.streamsParaGrid.length / this.pageSize) > 1;
  }

  @Input() public data: string[] = []; //ids de cámaras
  @Input() public zonaInicial: ICamaraAlarma;
  @Input() public fechaEvento?: string;
  @Input() public nombreEntidad?: string;
  @Input() public cantCamaras?: number;
  @Input() public popupMapa: boolean = false; // Si true, modo super compacto para popups de mapa

  // Getters calculados basados en popupMapa
  public get paginadorAbajo(): boolean {
    return this.popupMapa || this.header;
  }

  public get colapsarPaginador(): boolean {
    return this.popupMapa;
  }

  // Getter para el título dinámico
  public get tituloComponente(): string {
    if (this.nombreEntidad) {
      return this.nombreEntidad;
    }

    if (this.camarasRes && this.camarasRes.length > 0) {
      const firstCamera = this.camarasRes[0].camara;
      if (firstCamera?.identificacion) {
        return firstCamera.identificacion;
      }
    }

    const firstCamera = this.streamsParaGrid[0]?.camara;
    if (firstCamera?.identificacion) {
      return firstCamera.identificacion;
    }

    return 'Cámaras'; // Título por defecto
  }

  constructor() {
    const dataDialog = this.dataDialog;

    if (dataDialog) {
      this.data = dataDialog.data;
      this.zonaInicial = dataDialog.zonaInicial;
      this.fechaEvento = dataDialog.fechaEvento;
      this.nombreEntidad = dataDialog.nombreEntidad;
      this.header = true;
      this.popupMapa = dataDialog.popupMapa ?? false;
    }
  }

  //

  public async listarCamaras(): Promise<void> {
    this.camarasRes = [];
    this.streamsParaGrid = [];
    this.currentPageIndex = 0;

    const filter: IFilter<ICamara> = {
      _id: { $in: this.data },
    };
    const query: IQueryParam = {
      includeChildren: true,
      filter: JSON.stringify(filter),
    };
    const data = await firstValueFrom(this.lista2.camaras(query));
    data.datos.forEach((camara) => {
      camara.canales.forEach((canal) => {
        this.camarasRes.push({
          camara: camara,
          canal: canal.numero,
          tipo: camara.tipo,
        });
        this.streamsParaGrid.push({
          camara: camara,
          canal: canal.numero,
          streamUrl: undefined,
          loading: false,
          error: false,
        });
      });
    });
    this.initAfterLoad();
  }

  public select(camara?: ICamara, canal?: string) {
    // Si solo hay un stream, no hacer nada (no alternar maximizado)
    if (this.streamsParaGrid.length <= 1) {
      return;
    }

    this.maximizado = !this.maximizado;
    if (this.maximizado) {
      this.camaraActual = camara;
      this.canalActual = canal;
    } else {
      this.camaraActual = null;
      this.canalActual = null;
      this.fecha = null;
      this.vivo = true;
      this.fechaSeleccionadaISO = null;
    }
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public volverAtras(min: number) {
    // this.fecha = null;
    this.dragBoundary = document.getElementById(this.nombreEntidad);

    // Para el botón "Hora Evento" (min === 0), no mostrar el video pequeño en vivo
    // Para otros botones de tiempo, sí mostrar el video pequeño en vivo
    this.vivo = min === 0 ? true : false;

    // Obtener la fecha actual del evento
    const tiempo = new Date(this.fechaEvento);

    // Restar los minutos especificados
    if (min > 0) {
      tiempo.setMinutes(tiempo.getMinutes() - min);
    }

    // La API espera el instante en UTC y ella misma lo pasa a la hora del
    // equipo (resta 3 h). fechaEvento ya es un instante (ISO con Z), así que
    // no se ajusta acá: restarle 3 h más hacía que "Hora Evento" y los
    // rebobinados mostraran la grabación 3 horas antes del evento.

    setTimeout(() => {
      const nuevaFecha = tiempo.toISOString();
      // Si la fecha es la misma que la actual, restarle un segundo para forzar el cambio
      if (this.fecha === nuevaFecha) {
        tiempo.setSeconds(tiempo.getSeconds() - 1);
        this.fecha = tiempo.toISOString();
      } else {
        this.fecha = nuevaFecha;
      }
    }, 10);
  }

  public onFechaPickerChange(value: string | null): void {
    if (!value) {
      this.fecha = null;
      this.vivo = true;
    }
  }

  public volverAlVivo(): void {
    this.fecha = null;
    this.vivo = true;
    this.fechaSeleccionadaISO = null;
  }

  public irAFechaSeleccionada(): void {
    if (!this.fechaSeleccionadaISO) return;

    this.dragBoundary = document.getElementById(this.nombreEntidad);
    this.vivo = false; // Mostrar video grabado

    // Tomar la fecha seleccionada del picker
    const tiempo = new Date(this.fechaSeleccionadaISO);

    setTimeout(() => {
      const nuevaFecha = tiempo.toISOString();
      // Si la fecha es la misma que la actual, restarle un segundo para forzar el cambio
      if (this.fecha === nuevaFecha) {
        tiempo.setSeconds(tiempo.getSeconds() - 1);
        this.fecha = tiempo.toISOString();
      } else {
        this.fecha = nuevaFecha;
      }
    }, 10);
  }

  // Un clic en cualquier parte del diálogo lo trae al frente. Va en el host y
  // no como (click) en el contenedor de la plantilla: es una superficie, no un
  // botón.
  @HostListener('click')
  public alClicEnDialogo(): void {
    this.bringToFront();
  }

  public bringToFront(): void {
    const dialogContainerTagName = 'MAT-DIALOG-CONTAINER';
    const parentEl = this.el.nativeElement.closest(dialogContainerTagName);
    if (!parentEl) return;

    const dialogId = parentEl.getAttribute('id');
    const dialogsContainer = Array.from(
      this.cdk.getContainerElement().childNodes,
    );
    const previousDialog = this.cdk.getContainerElement().lastElementChild;
    const focusedDialog = (dialogsContainer as HTMLElement[]).find(
      (el: HTMLElement) => {
        return el.querySelector(`#${dialogId}`);
      },
    );
    // para evitar que el scroll se resetee si es el mismo dialog
    if (focusedDialog !== previousDialog) {
      if (focusedDialog) {
        // appendChild moves the element to the end of the container
        this.cdk.getContainerElement().appendChild(focusedDialog);
      }
    }
  }

  async ngOnInit() {
    // Si se abre como dialog con paramsService (flujo antiguo)

    if (
      this.dialogRef &&
      this.paramsService.getParams() &&
      this.paramsService.getParams()['dialog']
    ) {
      this.header = true;
      const cam: ICamara = this.paramsService.getParams()['camara'][0];
      if (cam) {
        cam.canales.map((canal) => {
          this.camarasRes.push({
            camara: cam,
            canal: canal.numero,
            tipo: cam.tipo,
          });
          this.streamsParaGrid.push({
            camara: cam,
            canal: canal.numero,
            streamUrl: undefined,
            loading: false,
            error: false,
          });
        });
        this.initAfterLoad();
      }
    }
    // Si tiene data (ya sea por @Input o por MAT_DIALOG_DATA)
    else if (this.data && this.data.length > 0) {
      await this.listarCamaras();
    }

    if (this.zonaInicial) {
      const camara = this.camarasRes.find(
        (c) => c.camara._id === this.zonaInicial.idCamara,
      )?.camara;
      const canal = camara?.canales.find(
        (can) => can.numero === this.zonaInicial.canal,
      );
      if (camara && canal) {
        this.camaraActual = camara;
        this.canalActual = canal.numero;
        this.maximizado = true;
      }
    }

    if (this.abrirEnMosaico()) {
      this.mosaico = true;
      // Cámara puntual (zona del evento): el mosaico abre en modo 1 cámara
      // sobre ella, con los botones de grabación del evento, en vez de la
      // vista maximizada de la grilla.
      if (this.maximizado && this.camaraActual) {
        this.mosaicoInicial = {
          idCamara: this.camaraActual._id,
          canal: this.canalActual,
        };
        this.maximizado = false;
        this.camaraActual = null;
        this.canalActual = null;
      }
    }
    this.loading = false;
  }

  /**
   * Vista inicial: mosaico, también desde eventos (el mosaico tiene los
   * botones "Hora Evento" / -30s / -1m… en su modo de 1 cámara). Salvo:
   * - una sola cámara/canal → vista maximizada (no hay nada que armar);
   * - popup del mapa → modo compacto, el mosaico (75vh) no entra.
   */
  private abrirEnMosaico(): boolean {
    return this.streamsParaGrid.length > 1 && !this.popupMapa;
  }

  private initAfterLoad(): void {
    this.currentPageIndex = 0;
    if (this.streamsParaGrid.length === 1) {
      this.maximizado = true;
      this.camaraActual = this.streamsParaGrid[0].camara;
      this.canalActual = this.streamsParaGrid[0].canal;
    }
  }
}
